import type { ReactNode } from 'react';

import type { ServerSummary } from '@ottd/api';

import { tr } from '@/lib/locale';
import { PlayerLink, RecordTimeLink } from '@/pages/shared';

export interface ServerFact {
  key: string;
  value: ReactNode;
  /** Tailwind text colour class for the value. */
  tone: string;
}

/**
 * The best-time row's value: `3h 30m 10s by Yorki`, with the time linked to
 * the record game (gold) and the holder to their profile (green). Drops the
 * "by" clause when the record predates player attribution (`winner_player_id`
 * is nullable).
 */
function bestTime(server: ServerSummary): ReactNode {
  if (!server.bestTime) return tr('server:facts.noRecord');
  const time = (
    <RecordTimeLink time={server.bestTime} gameId={server.recordGameId} className="text-gold" />
  );
  if (!server.recordHolder) return time;

  return (
    <>
      {time} {tr('server:facts.bestTimeBy')} <PlayerLink name={server.recordHolder} />
    </>
  );
}

/**
 * The label/value rows both the server list card and the server page show:
 * goal, best time (with record holder), map size and address.
 */
export function serverFacts(server: ServerSummary): ServerFact[] {
  return [
    { key: tr('server:facts.goal'), value: server.goal ?? '-', tone: 'text-green' },
    {
      key: tr('server:facts.bestTime'),
      value: bestTime(server),
      // The time carries its own gold; "by" reads in the body colour.
      tone: server.bestTime ? 'text-text' : 'text-dim',
    },
    { key: tr('server:facts.mapSize'), value: server.mapSize ?? '-', tone: 'text-text' },
    { key: tr('server:facts.address'), value: server.address ?? '-', tone: 'text-orange' },
  ];
}
